import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { DashboardLayout } from '../components/Layout'
import { Button, Badge, StatusBadge, Input, Select, Spinner } from '../components/UI'
import { MapPin, Filter, X, Eye, DollarSign, Ruler, Heart, Search, Sliders } from 'lucide-react'

const AD_TYPES = ['billboard', 'digital_screen', 'bus_shelter', 'unipole', 'lamp_post', 'building_wrap', 'mall_display', 'other']
const AREAS = ['Sheikh Zayed Road', 'Dubai Marina', 'JLT', 'Business Bay', 'DIFC', 'Deira', 'Bur Dubai', 'JBR', 'Palm Jumeirah', 'Downtown Dubai']

function AdSpaceCard({ space, onBook, onSave, saved }) {
  const navigate = useNavigate()
  const img = space.photos?.[0] || `https://images.unsplash.com/photo-1617550523898-b2b67b3d5b16?w=400&q=80`

  return (
    <div className="ad-card" onClick={() => navigate(`/spaces/${space.id}`)}>
      <div className="ad-card-image">
        <img src={img} alt={space.title} />
        <div className="ad-card-type-badge">
          <Badge variant="navy" style={{ textTransform: 'capitalize' }}>
            {space.type?.replace('_', ' ')}
          </Badge>
        </div>
        <button
          className={`ad-card-save-btn ${saved ? 'saved' : ''}`}
          onClick={(e) => { e.stopPropagation(); onSave(space.id) }}
        >
          <Heart size={16} fill={saved ? 'currentColor' : 'none'} />
        </button>
        {space.is_digital && (
          <div style={{ position: 'absolute', bottom: 10, left: 12 }}>
            <Badge variant="info">Digital</Badge>
          </div>
        )}
      </div>
      <div className="ad-card-body">
        <div className="ad-card-title">{space.title}</div>
        <div className="ad-card-location"><MapPin size={11} /> {space.area}, {space.city}</div>
        <div className="ad-card-stats">
          {space.daily_impressions && (
            <div className="ad-card-stat">
              <span className="ad-card-stat-value">{(space.daily_impressions / 1000).toFixed(0)}k</span>
              <span className="ad-card-stat-label">Daily Views</span>
            </div>
          )}
          {space.width_meters && space.height_meters && (
            <div className="ad-card-stat">
              <span className="ad-card-stat-value">{space.width_meters}×{space.height_meters}m</span>
              <span className="ad-card-stat-label">Dimensions</span>
            </div>
          )}
          <div className="ad-card-stat">
            <span className="ad-card-stat-value">{space.minimum_booking_days || 7}d</span>
            <span className="ad-card-stat-label">Min. Booking</span>
          </div>
        </div>
        <div className="ad-card-footer">
          <div>
            <div className="ad-card-price">
              {space.price_per_month?.toLocaleString()} AED <span>/month</span>
            </div>
            {space.price_per_week && (
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                or {space.price_per_week?.toLocaleString()} AED/week
              </div>
            )}
          </div>
          <Button
            size="sm"
            onClick={(e) => { e.stopPropagation(); onBook(space) }}
          >
            Book Now
          </Button>
        </div>
      </div>
    </div>
  )
}

// Simple map visualization without mapbox (uses OpenStreetMap iframe approach)
function MapView({ spaces, selectedId, onSelect }) {
  const center = spaces.length > 0
    ? { lat: spaces.reduce((s, sp) => s + sp.latitude, 0) / spaces.length, lng: spaces.reduce((s, sp) => s + sp.longitude, 0) / spaces.length }
    : { lat: 25.2048, lng: 55.2708 }

  return (
    <div style={{ flex: 1, position: 'relative', borderRadius: 'var(--radius-lg)', overflow: 'hidden', background: 'var(--gray-200)', minHeight: 400 }}>
      {/* OpenStreetMap embed */}
      <iframe
        title="Map"
        style={{ width: '100%', height: '100%', border: 'none', minHeight: 500 }}
        src={`https://www.openstreetmap.org/export/embed.html?bbox=${center.lng - 0.15}%2C${center.lat - 0.1}%2C${center.lng + 0.15}%2C${center.lat + 0.1}&layer=mapnik&marker=${center.lat}%2C${center.lng}`}
      />

      {/* Overlay with pins */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <div style={{
          background: 'rgba(13,27,62,0.85)', backdropFilter: 'blur(8px)',
          borderRadius: 'var(--radius)', padding: '12px 20px', color: 'white',
          fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.9rem',
          display: 'flex', alignItems: 'center', gap: 8
        }}>
          <MapPin size={16} style={{ color: 'var(--accent)' }} />
          {spaces.length} spaces available in Dubai
        </div>
      </div>

      {/* Note about Mapbox */}
      <div style={{
        position: 'absolute', bottom: 12, left: 12, right: 12,
        background: 'rgba(255,255,255,0.95)', borderRadius: 8, padding: '8px 12px',
        fontSize: '0.78rem', color: 'var(--gray-600)', display: 'flex', alignItems: 'center', gap: 6
      }}>
        ℹ️ For interactive map with custom pins, add your Mapbox API key to <code style={{ background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 4 }}>REACT_APP_MAPBOX_TOKEN</code>
      </div>
    </div>
  )
}

export default function ExplorePage() {
  const navigate = useNavigate()
  const [spaces, setSpaces] = useState([])
  const [savedIds, setSavedIds] = useState(new Set())
  const [loading, setLoading] = useState(true)
  const [selectedSpace, setSelectedSpace] = useState(null)
  const [showFilters, setShowFilters] = useState(false)
  const [view, setView] = useState('split') // split | list | map
  const [filters, setFilters] = useState({
    search: '', type: '', area: '', minPrice: '', maxPrice: '', is_digital: ''
  })

  useEffect(() => { loadSpaces() }, [])

  async function loadSpaces() {
    setLoading(true)
    let q = supabase.from('ad_spaces').select('*').eq('is_active', true).eq('is_available', true)
    if (filters.type) q = q.eq('type', filters.type)
    if (filters.area) q = q.eq('area', filters.area)
    if (filters.minPrice) q = q.gte('price_per_month', parseFloat(filters.minPrice))
    if (filters.maxPrice) q = q.lte('price_per_month', parseFloat(filters.maxPrice))
    if (filters.is_digital === 'yes') q = q.eq('is_digital', true)
    if (filters.search) q = q.ilike('title', `%${filters.search}%`)

    const { data } = await q.order('created_at', { ascending: false })
    setSpaces(data || [])
    setLoading(false)
  }

  async function handleSave(spaceId) {
    const newSaved = new Set(savedIds)
    if (newSaved.has(spaceId)) newSaved.delete(spaceId)
    else newSaved.add(spaceId)
    setSavedIds(newSaved)
  }

  function handleBook(space) {
    navigate(`/spaces/${space.id}`)
  }

  return (
    <DashboardLayout title="Explore Spaces">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: 'calc(100vh - 130px)' }}>
        {/* Search + Filter bar */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Input
              placeholder="Search by location or space name..."
              value={filters.search}
              onChange={e => setFilters(p => ({ ...p, search: e.target.value }))}
              icon={<Search size={16} />}
            />
          </div>

          <Select value={filters.type} onChange={e => setFilters(p => ({ ...p, type: e.target.value }))}
            style={{ width: 160 }}>
            <option value="">All Types</option>
            {AD_TYPES.map(t => <option key={t} value={t} style={{ textTransform: 'capitalize' }}>{t.replace('_', ' ')}</option>)}
          </Select>

          <Select value={filters.area} onChange={e => setFilters(p => ({ ...p, area: e.target.value }))}
            style={{ width: 180 }}>
            <option value="">All Areas</option>
            {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
          </Select>

          <Button variant="outline" icon={<Sliders size={16} />} onClick={() => setShowFilters(!showFilters)}>
            More Filters
          </Button>

          <Button onClick={loadSpaces} icon={<Search size={16} />}>Search</Button>
        </div>

        {/* Advanced filters */}
        {showFilters && (
          <div className="card" style={{ padding: 16 }} className="animate-slideDown">
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <Input label="Min Price (AED/month)" type="number" placeholder="0" value={filters.minPrice}
                onChange={e => setFilters(p => ({ ...p, minPrice: e.target.value }))} style={{ width: 160 }} />
              <Input label="Max Price (AED/month)" type="number" placeholder="100,000" value={filters.maxPrice}
                onChange={e => setFilters(p => ({ ...p, maxPrice: e.target.value }))} style={{ width: 160 }} />
              <Select label="Digital Only" value={filters.is_digital}
                onChange={e => setFilters(p => ({ ...p, is_digital: e.target.value }))} style={{ width: 140 }}>
                <option value="">All</option>
                <option value="yes">Digital Only</option>
                <option value="no">Static Only</option>
              </Select>
              <Button variant="ghost" size="sm" icon={<X size={14} />}
                onClick={() => { setFilters({ search: '', type: '', area: '', minPrice: '', maxPrice: '', is_digital: '' }); setShowFilters(false) }}>
                Clear All
              </Button>
            </div>
          </div>
        )}

        {/* Results count + view toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: '0.88rem', color: 'var(--gray-500)' }}>
            {loading ? 'Searching...' : `${spaces.length} spaces found`}
          </div>
          <div style={{ display: 'flex', gap: 4, background: 'var(--gray-100)', borderRadius: 8, padding: 3 }}>
            {[['split', '⚡ Split'], ['list', '☰ List'], ['map', '🗺 Map']].map(([v, label]) => (
              <button key={v} onClick={() => setView(v)} style={{
                padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                background: view === v ? 'white' : 'transparent',
                color: view === v ? 'var(--navy)' : 'var(--gray-500)',
                fontWeight: view === v ? 600 : 400, fontSize: '0.82rem',
                boxShadow: view === v ? 'var(--shadow-sm)' : 'none', transition: 'all 0.15s'
              }}>{label}</button>
            ))}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Spinner size={36} />
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 20, flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {/* List panel */}
            {(view === 'split' || view === 'list') && (
              <div style={{
                width: view === 'split' ? '42%' : '100%',
                overflowY: 'auto',
                display: 'grid',
                gridTemplateColumns: view === 'list' ? 'repeat(auto-fill, minmax(300px, 1fr))' : '1fr',
                gap: 16,
                alignContent: 'start'
              }}>
                {spaces.length === 0 ? (
                  <div style={{ gridColumn: '1/-1', padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>
                    No spaces match your filters. Try adjusting your search.
                  </div>
                ) : spaces.map(space => (
                  <AdSpaceCard
                    key={space.id}
                    space={space}
                    onBook={handleBook}
                    onSave={handleSave}
                    saved={savedIds.has(space.id)}
                  />
                ))}
              </div>
            )}

            {/* Map panel */}
            {(view === 'split' || view === 'map') && (
              <div style={{ flex: 1, minHeight: 0 }}>
                <MapView spaces={spaces} selectedId={selectedSpace?.id} onSelect={setSelectedSpace} />
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
